import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import EnhancedPulseTokenCard from "../components/pulse/EnhancedPulseTokenCard";
import TransparentHeader from "../components/layout/TransparentHeader";
import TrackChannelModal from "../components/pulse/TrackChannelModal";
import UnlockSignalsModal from "../components/pulse/UnlockSignalsModal";
import { getPulseTokens } from "../services/pulse-api";
import { checkTelegramScraperHealth } from "../services/telegram-api";
import { formatAge } from "../utils/formatters";
import { useAuth } from "../contexts/AuthContext";

// Whitelist of usernames/emails that have full access
const SIGNAL_WHITELIST = ['ghosid'];

const PulsePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const chainId = searchParams.get("chain") || "solana";

  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scraperConnected, setScraperConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [newSignalsCount, setNewSignalsCount] = useState(0);
  const [newSignalCAs, setNewSignalCAs] = useState(new Set()); // Track which signals are new
  
  // Channel group state (default to 'trayd', persist in localStorage)
  const [activeChannelGroup, setActiveChannelGroup] = useState(() => {
    const saved = localStorage.getItem('pulse_active_channel_group');
    return saved || 'trayd';
  });
  const [trackChannelModalOpen, setTrackChannelModalOpen] = useState(false);
  const [unlockModalOpen, setUnlockModalOpen] = useState(false);
  
  // Get user from auth context
  const { user } = useAuth();
  
  // Check if user is whitelisted
  const isWhitelisted = () => {
    if (!user) return false;
    const userEmail = user.email?.toLowerCase() || '';
    const username = userEmail.split('@')[0]; // Extract username from email
    return SIGNAL_WHITELIST.some(whitelisted => 
      userEmail.includes(whitelisted.toLowerCase()) || 
      username === whitelisted.toLowerCase()
    );
  };
  
  const pollingInterval = useRef(null);
  const heroRef = useRef(null);
  const previousSignalsRef = useRef(new Set()); // Track which signals we've seen
  const newSignalsTimeoutRef = useRef(null); // Track setTimeout for cleanup
  const isMountedRef = useRef(true); // Track if component is mounted
  const fetchInProgressRef = useRef(false); // Prevent race conditions

  // Format time ago for Telegram timestamp
  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return "Unknown";
    const ts = typeof timestamp === "number" ? timestamp : parseInt(timestamp);
    const milliseconds = ts > 1e12 ? ts : ts * 1000;
    return formatAge(milliseconds);
  };

  // Check scraper health on mount
  useEffect(() => {
    checkHealth();
  }, []);

  // Save activeChannelGroup to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('pulse_active_channel_group', activeChannelGroup);
  }, [activeChannelGroup]);

  // Clear signals when channel group changes
  useEffect(() => {
    console.log(`🔄 Channel group changed to: ${activeChannelGroup}, clearing signals...`);
    setSignals([]);
    setLoading(true);
    setError(null);
    setNewSignalsCount(0);
    setNewSignalCAs(new Set());
    previousSignalsRef.current = new Set();
    fetchInProgressRef.current = false; // Reset fetch flag to allow new fetch
  }, [activeChannelGroup]);

  // Fetch signals on mount and set up polling
  useEffect(() => {
    isMountedRef.current = true;
    fetchSignals(true); // Initial load

    // Poll for new signals every 30 seconds
    pollingInterval.current = setInterval(() => {
      if (isMountedRef.current) {
        fetchSignals(false); // Subsequent loads (merge new tokens)
      }
    }, 30000);

    return () => {
      isMountedRef.current = false;
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
      if (newSignalsTimeoutRef.current) {
        clearTimeout(newSignalsTimeoutRef.current);
      }
    };
  }, [chainId, activeChannelGroup]);

  const checkHealth = async () => {
    try {
      const healthy = await checkTelegramScraperHealth();
      setScraperConnected(healthy);
    } catch (err) {
      console.error("Error checking scraper health:", err);
      setScraperConnected(false);
    }
  };

  const fetchSignals = async (isInitialLoad = false) => {
    // Prevent race conditions - don't start new fetch if one is in progress
    if (fetchInProgressRef.current) {
      console.log("⏸️ Fetch already in progress, skipping...");
      return;
    }

    fetchInProgressRef.current = true;

    if (isInitialLoad) {
      setLoading(true);
    }
    setError(null);
    
    console.log(`🔍 Fetching signals for channel group: ${activeChannelGroup}, chain: ${chainId}`);
    
    try {
      const fetchedSignals = await getPulseTokens(chainId, activeChannelGroup);
      console.log(`📡 Received ${fetchedSignals.length} signals for channel group: ${activeChannelGroup}`);
      
      // Check if component is still mounted before updating state
      if (!isMountedRef.current) {
        return;
      }
      
      // Create a Set of current signal CAs for quick lookup
      const currentSignalCAs = new Set(fetchedSignals.map(s => s.ca));
      
      // Limit to 8 tokens max
      const MAX_TOKENS = 8;
      const limitedSignals = fetchedSignals.slice(0, MAX_TOKENS);
      
      // Use functional state update to avoid stale closure issues
      setSignals((prevSignals) => {
        if (isInitialLoad || prevSignals.length === 0) {
          // Initial load - set signals (max 8)
          previousSignalsRef.current = new Set(limitedSignals.map(s => s.ca));
          return limitedSignals;
        } else {
          // Subsequent loads - rotation logic
          const existingCAs = new Set(prevSignals.map(s => s.ca));
          const newSignals = limitedSignals.filter(s => !existingCAs.has(s.ca));
          
          if (newSignals.length > 0) {
            console.log(`🆕 ${newSignals.length} new token(s) detected!`);
            setNewSignalsCount(newSignals.length);
            
            // Track which CAs are new
            const newCAs = new Set(newSignals.map(s => s.ca));
            setNewSignalCAs(newCAs);
            
            // Clear any existing timeout
            if (newSignalsTimeoutRef.current) {
              clearTimeout(newSignalsTimeoutRef.current);
            }
            
            // Clear new signals indicators after 5 seconds
            newSignalsTimeoutRef.current = setTimeout(() => {
              if (isMountedRef.current) {
                setNewSignalsCount(0);
                setNewSignalCAs(new Set());
              }
            }, 5000);
            
            // Rotation logic: new tokens at position 1, remove last token(s) if at max
            let updatedSignals = [...newSignals, ...prevSignals];
            
            // Remove duplicates (keep first occurrence)
            const seen = new Set();
            updatedSignals = updatedSignals.filter(s => {
              if (seen.has(s.ca)) {
                return false;
              }
              seen.add(s.ca);
              return true;
            });
            
            // Limit to MAX_TOKENS (remove oldest/last tokens if over limit)
            if (updatedSignals.length > MAX_TOKENS) {
              const removed = updatedSignals.slice(MAX_TOKENS);
              console.log(`🗑️ Removed ${removed.length} token(s) (rotation limit: ${MAX_TOKENS})`);
              updatedSignals = updatedSignals.slice(0, MAX_TOKENS);
            }
            
            previousSignalsRef.current = new Set(limitedSignals.map(s => s.ca));
            return updatedSignals;
          } else {
            // No new signals, but update existing ones (in case data changed)
            // Still limit to MAX_TOKENS
            setNewSignalCAs(new Set()); // Clear any old "new" indicators
            previousSignalsRef.current = new Set(limitedSignals.map(s => s.ca));
            return limitedSignals;
          }
        }
      });
      
      if (isMountedRef.current) {
        setLastUpdate(new Date());
        await checkHealth();
      }
    } catch (err) {
      console.error("Error fetching pulse signals:", err);
      if (isMountedRef.current) {
        setError(
          err.message || "Failed to load signals. Make sure the Telegram scraper is running."
        );
        setScraperConnected(false);
      }
    } finally {
      fetchInProgressRef.current = false;
      if (isInitialLoad && isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const handleSignalClick = (signal) => {
    // Prevent navigation if signal is locked (server-side validation)
    if (signal.isLocked) {
      handleLockedSignalClick();
      return;
    }
    
    const ca = signal.ca || signal.tokenAddress;
    
    if (!ca) {
      console.error("Signal has no contract address:", signal);
      return;
    }
    
    const getChainPath = (chainId) => {
      const chainPathMap = {
        ethereum: "0x1",
        binance: "0x38",
        bsc: "0x38",
        polygon: "0x89",
        solana: "solana",
        arbitrum: "0xa4b1",
        base: "0x2105",
        avalanche: "0xa86a",
        optimism: "0xa",
        linea: "0xe708",
        fantom: "0xfa",
        pulse: "0x171",
        ronin: "0x7e4",
      };
      return chainPathMap[chainId] || chainId;
    };
    
    const chainPath = getChainPath(chainId);
    
    // Pass token data in navigation state including pairAddress if available
    navigate(`/${chainPath}/${ca}`, {
      state: {
        tokenData: {
          address: ca,
          name: signal.tokenName || null,
          symbol: signal.tokenSymbol || null,
          logo: signal.tokenImage || null,
          decimals: "18",
        },
        pairAddress: signal.pairAddress || null, // Pass pairAddress if we have it
        chainId: chainId,
      }
    });
  };

  // Split signals into 3 columns
  const getSignalsByColumn = (columnIndex) => {
    return signals.filter((_, index) => index % 3 === columnIndex);
  };
  
  // Handle locked signal click (show unlock modal)
  const handleLockedSignalClick = () => {
    setUnlockModalOpen(true);
  };
  
  // Determine if signal should be locked - use server-side status if available
  const isSignalLocked = (signal, signalIndex) => {
    // Prefer server-side lock status (prevents CSS manipulation attacks)
    if (signal.isLocked !== undefined) {
      return signal.isLocked;
    }
    
    // Fallback to client-side check (for backwards compatibility)
    // Only apply locking to trayd signals
    if (activeChannelGroup !== 'trayd') return false;
    
    // If user is whitelisted, no signals are locked
    if (isWhitelisted()) return false;
    
    // Lock signals beyond the first 2 (index 0 and 1)
    return signalIndex >= 2;
  };

  return (
    <div className="relative min-h-screen w-full overflow-x-hidden">
      {/* Background Image with Dim Overlay */}
      <div 
        className="fixed inset-0 z-0"
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL || ''}/background-image.png)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed'
        }}
      />
      {/* Dim Overlay - Darkens background so elements stand out */}
      <div className="fixed inset-0 z-0 bg-[#0a0e1a]/70" />
      {/* Teal Overlay - Muted teal tint */}
      <div className="fixed inset-0 z-0 bg-[#0d4d4d]/20" />
      
      {/* Transparent Header - Marinade Finance Style */}
      <TransparentHeader />
      
      {/* Main Content - Marinade Finance Style */}
      <div className="relative z-10">
        {/* Hero Section - Top of Fold */}
        <section ref={heroRef} className="relative min-h-screen flex flex-col pt-24 lg:pt-32 pb-16">
          {/* Hero Content */}
          <div className="relative z-20 px-6 sm:px-8 lg:px-12 pt-8 pb-12">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="flex-1"
                >
                  <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-pp-mori font-semibold text-white mb-4 leading-tight drop-shadow-lg">
                    Token Signals
                  </h1>
                  <p className="text-lg sm:text-xl text-white/80 font-inter max-w-2xl drop-shadow-md mb-6">
                    Discover tokens vetted from our proprietary filter geared for high win rate and low risk. Entire markets analyzed, 100k+ risk-managed wallets tracked, 90%+ win-rate validated, automation-safe strategies.
                  </p>
                  
                  {/* CTA Buttons */}
                  <div className="flex flex-wrap items-center gap-3 mt-4">
                    <button
                      onClick={() => setActiveChannelGroup('trayd')}
                      className={`px-5 py-2.5 rounded-full text-sm font-medium font-inter transition-all duration-200 ${
                        activeChannelGroup === 'trayd'
                          ? 'bg-white text-[#151a1a] shadow-lg'
                          : 'bg-white/10 text-white/80 hover:bg-white/20 border border-white/20'
                      }`}
                    >
                      Trayd Signals
                    </button>
                    <button
                      onClick={() => setActiveChannelGroup('kol')}
                      className={`px-5 py-2.5 rounded-full text-sm font-medium font-inter transition-all duration-200 ${
                        activeChannelGroup === 'kol'
                          ? 'bg-white text-[#151a1a] shadow-lg'
                          : 'bg-white/10 text-white/80 hover:bg-white/20 border border-white/20'
                      }`}
                    >
                      KOL Signals
                    </button>
                    <button
                      onClick={() => setActiveChannelGroup('gmgn')}
                      className={`px-5 py-2.5 rounded-full text-sm font-medium font-inter transition-all duration-200 ${
                        activeChannelGroup === 'gmgn'
                          ? 'bg-white text-[#151a1a] shadow-lg'
                          : 'bg-white/10 text-white/80 hover:bg-white/20 border border-white/20'
                      }`}
                    >
                      GMGN Signals
                    </button>
                    <button
                      onClick={() => setTrackChannelModalOpen(true)}
                      className="px-5 py-2.5 rounded-full text-sm font-medium font-inter transition-all duration-200 bg-white/10 text-white/80 hover:bg-white/20 border border-white/20"
                    >
                      Track A Channel
                    </button>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="flex flex-col sm:flex-row items-start sm:items-center gap-4"
                >
                  {/* Stats Cards - Marinade Style with Glass Effect */}
                  <div className="flex flex-col sm:flex-row gap-4">
                    {/* Active Signals */}
                    <div className="bg-white/95 backdrop-blur-md rounded-2xl px-6 py-4 shadow-lg border border-white/20">
                      <div className="text-2xl sm:text-3xl font-pp-mori font-semibold text-[#151a1a]">
                        {signals.length}
                      </div>
                      <div className="text-sm text-[#6b7280] font-inter mt-1">
                        Active Signals
                      </div>
                    </div>
                    
                    {/* Status indicator */}
                    <div className="bg-white/95 backdrop-blur-md rounded-2xl px-6 py-4 shadow-lg border border-white/20 flex items-center gap-3">
                      <div
                        className={`w-3 h-3 rounded-full ${
                          scraperConnected ? "bg-[#10b981] animate-pulse" : "bg-[#ef4444]"
                        }`}
                      />
                      <div>
                        <div className="text-sm font-pp-mori font-semibold text-[#151a1a]">
                          {scraperConnected ? "Live" : "Offline"}
                        </div>
                        {lastUpdate && (
                          <div className="text-xs text-[#6b7280] font-inter">
                            {formatTimeAgo(lastUpdate.getTime())}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* New tokens notification */}
                  {newSignalsCount > 0 && (
                    <motion.div
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0, opacity: 0 }}
                      className="px-5 py-3 rounded-xl bg-white/95 backdrop-blur-md text-[#151a1a] text-sm font-semibold font-inter shadow-lg border border-white/20"
                    >
                      🆕 {newSignalsCount} new token{newSignalsCount > 1 ? 's' : ''}
                    </motion.div>
                  )}
                </motion.div>
              </div>
            </div>
          </div>

          {/* Tokens Grid - Hero Section */}
          <div className="flex-1 px-6 sm:px-8 lg:px-12 pb-20">
            <div className="max-w-7xl mx-auto">
              {loading && signals.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex flex-col items-center justify-center min-h-[60vh]"
                >
                  <div className="relative">
                    <div className="w-16 h-16 border-4 border-[rgba(72,120,255,0.3)] border-t-[#4878ff] rounded-full animate-spin mb-6" />
                  </div>
                  <p className="text-xl text-white/80 font-inter">
                    Loading signals from Telegram channels...
                  </p>
                </motion.div>
              ) : error ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex flex-col items-center justify-center min-h-[60vh] max-w-md mx-auto"
                >
                  <div className="text-[#ef4444] mb-6 text-4xl">⚠️</div>
                  <div className="text-xl text-white mb-6 text-center font-inter">
                    {error}
                  </div>
                  <button
                    onClick={fetchSignals}
                    className="px-8 py-3 bg-white rounded-xl text-[#151a1a] font-semibold font-inter hover:bg-white/90 transition-colors"
                  >
                    Retry
                  </button>
                  <p className="text-sm text-white/70 mt-6 text-center font-inter">
                    Make sure the Telegram scraper is running on{" "}
                    {process.env.REACT_APP_TELEGRAM_SCRAPER_URL || "http://localhost:5000"}
                  </p>
                </motion.div>
              ) : signals.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex flex-col items-center justify-center min-h-[60vh]"
                >
                  <p className="text-xl text-white/80 mb-2 font-inter text-center">
                    No signals found for <span className="font-semibold capitalize">{activeChannelGroup}</span> channel group yet.
                  </p>
                  <p className="text-sm text-white/60 mb-6 font-inter text-center">
                    Make sure the Telegram scraper is running and receiving messages from the {activeChannelGroup} channels.
                  </p>
                  <button
                    onClick={() => fetchSignals(true)}
                    className="px-8 py-3 bg-white rounded-xl text-[#151a1a] font-semibold font-inter hover:bg-white/90 transition-colors"
                  >
                    Refresh
                  </button>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8 }}
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
                >
                  {/* Column 1 */}
                  <div className="space-y-6 lg:space-y-8">
                    {getSignalsByColumn(0).map((signal, index) => {
                      const actualIndex = index * 3;
                      const locked = isSignalLocked(signal, actualIndex);
                      return (
                        <EnhancedPulseTokenCard
                          key={`col1-${signal.ca}-${index}`}
                          signal={signal}
                          formatTimeAgo={formatTimeAgo}
                          onClick={() => handleSignalClick(signal)}
                          index={actualIndex}
                          isNew={newSignalCAs.has(signal.ca)}
                          chainId={chainId}
                          isLocked={locked}
                          onLockedClick={locked ? handleLockedSignalClick : null}
                        />
                      );
                    })}
                  </div>
                  {/* Column 2 */}
                  <div className="space-y-6 lg:space-y-8">
                    {getSignalsByColumn(1).map((signal, index) => {
                      const actualIndex = index * 3 + 1;
                      const locked = isSignalLocked(signal, actualIndex);
                      return (
                        <EnhancedPulseTokenCard
                          key={`col2-${signal.ca}-${index}`}
                          signal={signal}
                          formatTimeAgo={formatTimeAgo}
                          onClick={() => handleSignalClick(signal)}
                          index={actualIndex}
                          isNew={newSignalCAs.has(signal.ca)}
                          chainId={chainId}
                          isLocked={locked}
                          onLockedClick={locked ? handleLockedSignalClick : null}
                        />
                      );
                    })}
                  </div>
                  {/* Column 3 */}
                  <div className="space-y-6 lg:space-y-8">
                    {getSignalsByColumn(2).map((signal, index) => {
                      const actualIndex = index * 3 + 2;
                      const locked = isSignalLocked(signal, actualIndex);
                      return (
                        <EnhancedPulseTokenCard
                          key={`col3-${signal.ca}-${index}`}
                          signal={signal}
                          formatTimeAgo={formatTimeAgo}
                          onClick={() => handleSignalClick(signal)}
                          index={actualIndex}
                          isNew={newSignalCAs.has(signal.ca)}
                          chainId={chainId}
                          isLocked={locked}
                          onLockedClick={locked ? handleLockedSignalClick : null}
                        />
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </section>
      </div>
      <TrackChannelModal 
        isOpen={trackChannelModalOpen} 
        onClose={() => setTrackChannelModalOpen(false)} 
      />
      <UnlockSignalsModal 
        isOpen={unlockModalOpen} 
        onClose={() => setUnlockModalOpen(false)} 
      />
    </div>
  );
};

export default PulsePage;
